import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Label from './__partials/label';
import iconList from './svgs';

const iconTypes = Object.keys(iconList);
const iconTypesLowerCase = iconTypes.map(iconType => iconType.toLowerCase()).concat(['uidismiss']);

const iconMap = iconTypes.reduce((prev, curr) => {
  prev[curr.toLowerCase()] = iconList[curr];

  return prev;
}, {});

const Icon = ({
  children,
  size,
  type,
  color,
  className,
  style,
  isButtonIcon,
  title,
  showBrowserTooltip,
  label,
  labelPosition,
  labelParent,
  qaId,
}) => {
  let svgType = type;
  let fillColor = 'inherit';
  let svgSize = size;
  let svgViewbox = '0 0 32 32';
  let svgTitle = '';
  let svgTitleId = '';
  let IconComponent;

  // UI Control icons (prefixed with ui) should all be small unless otherwise marked.
  // Viewbox and size can differ because we want icons to carry same weight regardless of size.
  if (typeof type !== 'undefined') {
    if (svgType === 'uidismiss') {
      if (!svgSize || svgSize === 'small') {
        svgViewbox = '0 0 16 16';
        svgSize = 'small';
        svgType = 'uidismisssmall';
      } else if (svgSize === 'medium') {
        svgViewbox = '0 0 24 24';
        svgSize = 'medium';
        svgType = 'uidismissmedium';
      } else if (svgSize === 'large') {
        svgViewbox = '0 0 32 32';
        svgSize = 'large';
        svgType = 'uidismisslarge';
      }
    } else if (svgType.substring(0, 12) === 'uinavigation') {
      svgViewbox = '0 0 32 32';
      svgSize = 'large';
    } else if (svgType !== 'uireorder' && svgType !== 'uiexpandcontent' && svgType.substring(0, 2) === 'ui') {
      svgViewbox = '0 0 16 16';
      svgSize = 'small';
    }

    IconComponent = iconMap[svgType.toLowerCase()];

    if (!IconComponent) {
      return <span style={{ display: 'none' }}>{type.toLowerCase()} icon not found.</span>;
    }

    // File type icons are unique in that we always want them to use their
    // default fill and we want them to be sized large unless otherwise specified
    if (svgType.indexOf('filetype') > -1) {
      if (!size) {
        svgSize = 'large';
      }
      fillColor = undefined;
    }

    // This generates the title and the title id that is linked to the aria-labelledby attribute.
    // We do not want the title to include our style modifiers of 'filled' and 'outline'
    // Format: "GolfTitle"
    svgTitle = title ? title : type.replace('filled', '').replace('outline', '');
    svgTitleId = svgTitle + 'Title';
  } else {
    // A type was not provided, so we instead set the title and titleid for the children passed in.
    svgTitle = title;
    svgTitleId = svgTitle + 'Title';
  }

  const iconClasses = classNames({
    'uni-icon': !isButtonIcon,
    [`uni-icon--${svgSize}`]: !isButtonIcon && svgSize,
    'uni-btn__icon': isButtonIcon,
    [`uni-icon--${color}`]: color,
    'uni-icon--show-browser-tooltip': showBrowserTooltip,
    [className]: className !== undefined,
  });

  const svgEl = (
    <svg
      className={iconClasses}
      viewBox={svgViewbox}
      aria-labelledby={svgTitleId}
      role="img"
      fillRule="evenodd"
      style={style}
      data-qa-id={qaId}
    >
      <title id={svgTitleId}>{svgTitle}</title>
      {type && <IconComponent fill={fillColor} />}
      {children}
    </svg>
  );

  const iconEl = label ? (
    <Label label={label} position={labelPosition} parent={labelParent}>
      {svgEl}
    </Label>
  ) : (
      svgEl
    );

  return iconEl;
};

Icon.exists = type => !!iconMap[type.toLowerCase()];

Icon.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.oneOf(['contrast', 'default', 'subtle', 'nonessential']),
  className: PropTypes.string,
  style: PropTypes.object,
  isButtonIcon: PropTypes.bool,
  title: PropTypes.string,
  showBrowserTooltip: PropTypes.bool,
  label: PropTypes.string,
  labelPosition: PropTypes.oneOf(['top', 'bottom', 'right', 'left']),
  labelParent: PropTypes.string,
  qaId: PropTypes.string,
  type: (props, propName) => {
    if (props[propName] && iconTypesLowerCase.indexOf(props[propName].toLowerCase()) === -1) {
      return new Error(`${props[propName]} must be one of ${iconTypesLowerCase}`);
    }
  },
  children: PropTypes.element,
};

Icon.defaultProps = {
  showBrowserTooltip: false,
};

export default Icon;



// WEBPACK FOOTER //
// ./src/icon/index.jsx